import { fetchConfig } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { checkFaceValueLimit } from '@orchard/gift-card-limits';

/**
 * Orchard cart items extension.
 * Enhances Horizon's cart with:
 * - Care protection auto-removal when the protected product is removed
 * - Gift card face value limit enforcement on quantity changes
 */
export function initOrchardCart() {
  // Listen for cart updates to handle protection product removal
  document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate);
}

/**
 * When a cart item is removed, check if it had a linked care protection product.
 * If so, remove the protection product too.
 * @param {CartUpdateEvent} event
 */
async function handleCartUpdate(event) {
  const sections = event.detail?.data?.sections;
  if (!sections) return;

  // Parse the updated cart HTML to check for orphaned protection items
  try {
    const response = await fetch('/cart.js');
    const cart = await response.json();

    // Build a map of protection tokens from protected items
    const protectedTokens = new Set();
    const protectionItems = [];

    for (const item of cart.items) {
      const token = item.properties?.['_Care Protection No'];
      if (!token) continue;

      if (item.properties?.['_Care Protection Status'] === 'Accepted') {
        // This is the main product with protection
        protectedTokens.add(token);
      } else if (item.properties?.['Care Protection For']) {
        // This is a protection line item
        protectionItems.push({ item, token });
      }
    }

    // Find orphaned protection items (protection exists but main product was removed)
    for (const { item, token } of protectionItems) {
      if (!protectedTokens.has(token)) {
        // The main product was removed — remove the protection item
        await fetch(Theme.routes.cart_change_url, {
          ...fetchConfig('javascript'),
          body: JSON.stringify({
            id: item.key,
            quantity: 0,
          }),
        });
      }
    }
  } catch (err) {
    console.error('Orchard cart protection sync error:', err);
  }
}

/**
 * Validates gift card face value before allowing quantity increase.
 * Call this before processing quantity changes for limited gift cards.
 * @param {HTMLElement} lineItem - The cart line item element
 * @param {number} newQuantity - The requested new quantity
 * @returns {Promise<boolean>} Whether the quantity change is allowed
 */
export async function validateGiftCardQuantity(lineItem, newQuantity) {
  const isLimited = lineItem.dataset.limitedGiftCard === 'true';
  if (!isLimited) return true;

  const faceValue = parseInt(lineItem.dataset.giftCardFaceValue || '0', 10);
  if (faceValue <= 0) return true;

  const currentQuantity = parseInt(lineItem.dataset.currentQuantity || '0', 10);
  if (newQuantity <= currentQuantity) return true; // Always allow decreases

  const additionalQuantity = newQuantity - currentQuantity;
  const { allowed } = await checkFaceValueLimit(faceValue, additionalQuantity);

  return allowed;
}

// Auto-initialize
initOrchardCart();
